import type { ReactNode } from 'react'

export default function Terms() {
  return (
    <div className="container" style={{ maxWidth: 740, padding: '64px 24px 96px' }}>
      <div className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', letterSpacing: '0.1em', marginBottom: 16 }}>
        LEGAL · TERMS OF SERVICE
      </div>
      <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 8 }}>
        Terms of Service
      </h1>
      <p className="muted" style={{ fontSize: 13, marginBottom: 48 }}>
        Last updated: 6 May 2026 · Effective immediately
      </p>

      <Prose>
        <p>
          These Terms of Service ("Terms") govern your use of Instadash, operated by Codextech Pty Ltd
          ("Instadash", "we", "us"), a company registered in Victoria, Australia. By creating an account or
          using the service, you agree to these Terms. If you do not agree, do not use the service.
        </p>

        <Section title="1. The service">
          <p>
            Instadash provides a hosted data interface platform that accepts structured data (JSON, CSV, NDJSON,
            logs) via CLI or HTTP and returns hosted grids, queryable API endpoints, and LLM-ready context files.
            The service includes a web dashboard, a CLI tool, and API endpoints.
          </p>
        </Section>

        <Section title="2. Accounts">
          <p>
            You must provide accurate information when creating an account. You are responsible for all activity
            under your account. Keep your credentials and API keys secure. Notify us immediately at{' '}
            <a href="mailto:security@instadash.io">security@instadash.io</a> if you suspect unauthorised access.
          </p>
          <p>
            You must be at least 16 years old to use Instadash. By creating an account you represent that you
            meet this requirement.
          </p>
          <p>
            One person or legal entity may not maintain more than one free account. Organisations may have
            multiple paid seats.
          </p>
        </Section>

        <Section title="3. Acceptable use">
          <p>You agree not to use Instadash to:</p>
          <ul>
            <li>Store, transmit, or publish content that is illegal, harmful, or infringes third-party rights</li>
            <li>Upload personal data of others without a lawful basis under applicable privacy law</li>
            <li>Conduct automated attacks, scraping at scale, or denial-of-service attempts against the platform
              or any third party</li>
            <li>Reverse-engineer, circumvent rate limits, or access internal APIs not documented for public use</li>
            <li>Resell or repackage Instadash as a white-label product without a written agreement</li>
            <li>Use the service for cryptocurrency mining or other compute-intensive workloads outside normal
              data-grid use</li>
          </ul>
          <p>
            We reserve the right to suspend accounts that violate these rules, with or without notice depending
            on severity.
          </p>
        </Section>

        <Section title="4. Your data">
          <p>
            You retain full ownership of all data you push to Instadash. We claim no intellectual property rights
            over your content. You grant us a limited licence to store, process, and serve your data solely for
            the purpose of providing the service.
          </p>
          <p>
            Grids marked <strong>private</strong> are accessible only to you and anyone you explicitly share them
            with. Grids marked <strong>public</strong> are accessible to anyone with the URL, including
            unauthenticated users and LLM agents.
          </p>
          <p>
            You can export all your data at any time using <code>instadash export</code>. On account deletion,
            your data is removed within 30 days.
          </p>
        </Section>

        <Section title="5. Paid plans and billing">
          <p>
            Paid plans (Builder, Agent) are billed monthly via Stripe. Billing begins on the date you upgrade.
            Plans auto-renew unless cancelled before the next billing date.
          </p>
          <p>
            Prices are listed in USD and exclude applicable taxes. We will notify you at least 30 days before
            any price increase.
          </p>
          <p>
            <strong>Refunds:</strong> We offer a 7-day money-back guarantee for first-time paid subscriptions.
            Contact <a href="mailto:billing@instadash.io">billing@instadash.io</a> within 7 days of your first
            charge. After that, refunds are at our discretion.
          </p>
          <p>
            If a payment fails, we will retry for 7 days before downgrading your account to the free tier. Your
            data is preserved during this grace period.
          </p>
        </Section>

        <Section title="6. Free tier limits">
          <p>
            The free tier is provided at our discretion and may be modified or discontinued with 30 days notice.
            Free accounts that have been inactive for 12 months may be reclaimed after email notice.
          </p>
        </Section>

        <Section title="7. Service availability">
          <p>
            We aim for high availability but do not guarantee any specific uptime SLA on free or Builder plans.
            Agent and Platform plans include an SLA as defined in your order. We may perform maintenance with
            reasonable notice where possible.
          </p>
          <p>
            Current status is published at <code>instadash.io</code> (v1.0 · all systems nominal chip in the nav).
          </p>
        </Section>

        <Section title="8. Intellectual property">
          <p>
            Instadash, its logo, and associated software are owned by Codextech Pty Ltd and protected by
            Australian and international copyright and trademark law. You may not use our name, logo, or branding
            without prior written permission.
          </p>
          <p>
            The Instadash CLI is open-source under the MIT licence. The source is available on GitHub and may
            be used subject to those licence terms.
          </p>
        </Section>

        <Section title="9. Limitation of liability">
          <p>
            To the maximum extent permitted by law, Instadash is provided "as is" without warranties of any kind.
            We are not liable for data loss, business interruption, lost profits, or any indirect or consequential
            damages arising from your use of the service.
          </p>
          <p>
            Our total liability to you for any claim arising from these Terms or the service shall not exceed the
            greater of (a) the fees you paid us in the 12 months preceding the claim, or (b) AUD $100.
          </p>
          <p>
            Nothing in these Terms limits liability that cannot be excluded under the Australian Consumer Law,
            including consumer guarantees.
          </p>
        </Section>

        <Section title="10. Indemnification">
          <p>
            You agree to indemnify and hold harmless Codextech Pty Ltd and its employees from any claim, loss,
            or damage (including reasonable legal fees) arising from your use of the service, your content, or
            your violation of these Terms.
          </p>
        </Section>

        <Section title="11. Termination">
          <p>
            You may delete your account at any time from the workspace settings. We may suspend or terminate
            your account if you materially breach these Terms.
          </p>
          <p>
            On termination: your access ends immediately, grids are taken offline, and data is deleted within
            30 days. Billing obligations for the current period remain due.
          </p>
        </Section>

        <Section title="12. Changes to these Terms">
          <p>
            We may update these Terms from time to time. We will notify you by email at least 14 days before
            material changes take effect. Continued use of the service after the effective date constitutes
            acceptance. If you disagree with a change, you may terminate your account before it takes effect.
          </p>
        </Section>

        <Section title="13. Governing law">
          <p>
            These Terms are governed by the laws of Victoria, Australia. Any disputes shall be resolved in the
            courts of Victoria, Australia. If you are a consumer in the EU, you may also have rights under the
            laws of your country of residence.
          </p>
        </Section>

        <Section title="14. Contact">
          <p>
            Questions about these Terms:
          </p>
          <p>
            <strong>Codextech Pty Ltd</strong><br />
            Melbourne, Victoria, Australia<br />
            <a href="mailto:legal@instadash.io">legal@instadash.io</a>
          </p>
        </Section>
      </Prose>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ marginTop: 40 }}>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: 'var(--fg)' }}>{title}</h2>
      {children}
    </section>
  )
}

function Prose({ children }: { children: ReactNode }) {
  return (
    <div style={{
      fontSize: 14,
      lineHeight: 1.75,
      color: 'var(--fg-2)',
    }}>
      {children}
    </div>
  )
}
